import { Flex, Icon, Text } from '@chakra-ui/react';
import { Card } from '@ui/components/Card';
import { LPTokenIcon } from '@ui/components/LPTokenIcon';
import { ModalOrDrawerBody } from '@ui/components/ModalOrDrawer';
import { TokenIcon } from '@ui/components/TokenIcon';
import { useTranslations } from '@ui/i18n';
import { gardenTokenDict } from '@ui/utils/garden';
import { LiquidityStrategyTokenDict } from '@ui/utils/strategy';
import { VectorDown } from '@veno-app/icons';
import type { ReactNode } from 'react';
import { Fragment, useMemo } from 'react';

import { useStrategyFlowDrawer } from './useStrategyFlowDrawer';

export const DefaultContent = () => {
  const t = useTranslations();
  const isWithdraw = useStrategyFlowDrawer((s) => s.isWithdraw);
  const token = useStrategyFlowDrawer((s) => s.token);

  const tokenInfo = LiquidityStrategyTokenDict[token];

  const transferNodes = useMemo<ReactNode[]>(() => {
    const isFerroLp = token === 'ferroLP';
    const tokenNodeCopy = (() => {
      return isWithdraw
        ? t('The strategy transfers {coin} to your wallet', {
            coin: tokenInfo.name,
          })
        : t(
            'The strategy adds {coin} from your wallet as liquidity to the {platform} {poolName} Pool',
            {
              coin: tokenInfo.name,
              platform: gardenTokenDict.cro.FERRO.protocolName ?? '',
              poolName: gardenTokenDict.cro.FERRO.poolName ?? '',
            },
          );
    })();
    const tokenNode = (
      <Flex flexDir="column" alignItems="center">
        <TokenIcon
          token={token === 'lcro' ? 'lcro' : 'cro'}
          w="32px"
          h="32px"
        />
        <Text textStyle="bodySmall">{tokenInfo.name}</Text>
        <Text textStyle="bodyBold">{t('Wallet')}</Text>
        <Text textStyle="bodySmall" color="text-light">
          {tokenNodeCopy}
        </Text>
      </Flex>
    );
    const ferroLpNodeCopy = (() => {
      if (isWithdraw) {
        return isFerroLp
          ? t('The strategy transfers {coin} to your wallet', {
              coin: tokenInfo.name,
            })
          : t(
              'The strategy burns the {lpCoin} and removes {coin} liquidity from {platform}',
              {
                lpCoin: gardenTokenDict.cro.FERRO.name ?? '',
                coin: tokenInfo.name,
                platform: gardenTokenDict.cro.FERRO.protocolName ?? '',
              },
            );
      }
      return isFerroLp
        ? t('The strategy transfers {coin} from your wallet to the strategy', {
            coin: gardenTokenDict.cro.FERRO.name ?? '',
          })
        : t('{platform} mints {coin} and transfers it to the Strategy', {
            coin: gardenTokenDict.cro.FERRO.name ?? '',
            platform: gardenTokenDict.cro.FERRO.protocolName ?? '',
          });
    })();
    const ferroLpNode = (
      <Flex flexDir="column" alignItems="center">
        <LPTokenIcon
          coinMode="cro"
          gardenType="FERRO"
          iconWidth="32px"
          iconHeight="32px"
        />
        <Text textStyle="bodySmall">{gardenTokenDict.cro.FERRO.name}</Text>
        <Text textStyle="bodyBold">
          {isFerroLp ? t('Wallet') : gardenTokenDict.cro.FERRO.protocolName}
        </Text>
        <Text textStyle="bodySmall" color="text-light">
          {ferroLpNodeCopy}
        </Text>
      </Flex>
    );
    const vaultTokenNodeCopy = (() => {
      if (isWithdraw) {
        return t('The strategy burns the {coin} from your wallet', {
          coin: tokenInfo.vaultTokenName,
        });
      }
      return t('The strategy mints {coin} and transfers it to your Wallet', {
        coin: tokenInfo.vaultTokenName,
      });
    })();
    const vaultTokenNode = (
      <Flex flexDir="column" alignItems="center">
        <Text textStyle="bodySmall">{tokenInfo.vaultTokenName}</Text>
        <Text textStyle="bodyBold">Veno</Text>
        <Text textStyle="bodySmall" color="text-light">
          {vaultTokenNodeCopy}
        </Text>
      </Flex>
    );
    const children = isFerroLp
      ? [ferroLpNode, vaultTokenNode]
      : [tokenNode, ferroLpNode, vaultTokenNode];
    return isWithdraw ? children.reverse() : children;
  }, [isWithdraw, t, token, tokenInfo.name, tokenInfo.vaultTokenName]);

  return (
    <ModalOrDrawerBody>
      <Flex flexDir="column" textAlign="center">
        {transferNodes.map((node, i) => {
          return (
            <Fragment key={i}>
              {i > 0 && (
                <Icon
                  as={VectorDown}
                  boxSize="50px"
                  color="brand-pink"
                  alignSelf="center"
                />
              )}
              <Card bg="brand-shadowsLightPositive" p="8px 20px 12px">
                {node}
              </Card>
            </Fragment>
          );
        })}
      </Flex>
    </ModalOrDrawerBody>
  );
};
